import React, { useCallback, useState } from 'react';
import {
  Copy,
  Key,
  FileJson,
  Zap,
  Table,
  BookOpen,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../utils/cn';
import { API_CONFIG } from '../../constants';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'authentication', label: 'Authentication', icon: Key },
  { id: 'upsert-contact', label: 'Upsert Contact', icon: FileJson },
  { id: 'create-event', label: 'Create Event', icon: Zap },
  { id: 'quick-reference', label: 'Quick Reference', icon: Table },
] as const;

function CodeBlock({
  children,
  language = 'text',
  className,
}: {
  children: string;
  language?: string;
  className?: string;
}) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    showToast({
      type: 'success',
      title: 'Copied',
      message: 'Code copied to clipboard',
    });
    setTimeout(() => setCopied(false), 2000);
  }, [children, showToast]);

  return (
    <div className={cn('relative group rounded-lg overflow-hidden', className)}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {language}
        </span>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--background))] transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm font-mono bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-t-0">
        <code className="text-foreground">{children}</code>
      </pre>
    </div>
  );
}

function DocTable({
  headers,
  rows,
  className,
}: {
  headers: string[];
  rows: (string | React.ReactNode)[][];
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-[hsl(var(--border))]',
        className
      )}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left font-semibold text-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted))]/50"
            >
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-muted-foreground">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodBadge({ method }: { method: string }) {
  const color =
    method === 'POST'
      ? 'bg-[hsl(var(--success-background))] text-[hsl(var(--success))]'
      : 'bg-[hsl(var(--info-background))] text-[hsl(var(--info))]';
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold',
        color
      )}
    >
      {method}
    </span>
  );
}

const ApiDocs: React.FC = () => {
  return (
    <div className="flex min-h-0 flex-1 h-[calc(99vh-50px)]">
      {/* Sidebar nav (desktop) */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="sticky top-0 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            API v2 Reference
          </h2>
          <nav className="space-y-0.5">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <a
                key={id}
                href={`#${id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition-colors"
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </a>
            ))}
          </nav>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 lg:px-10">
          {/* Hero */}
          <header className="mb-10">
            <h1 className="text-3xl font-bold text-foreground tracking-tight">
              API v2 – Developer Documentation
            </h1>
            <p className="mt-2 text-muted-foreground">
              Contacts and Events APIs for server-to-server integration. All
              requests use API Key authentication.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary-background))] px-3 py-2 text-sm">
                <span className="text-muted-foreground">Base URL</span>
                <code className="font-mono text-primary">
                  {API_CONFIG.SERVER_API_URL.replace(/\/$/, '')}
                </code>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-[hsl(var(--muted))] px-3 py-2 text-sm">
                <Key className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Auth</span>
                <code className="font-mono text-foreground">x-api-key</code>
              </div>
            </div>
          </header>

          {/* Overview */}
          <section id="overview" className="scroll-mt-6 mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Overview
            </h2>
            <p className="text-muted-foreground mb-4">
              This document describes the <strong>Contacts</strong> and{' '}
              <strong>Events</strong> APIs (v2). Both use API Key authentication
              and are intended for server-to-server integration.
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">Base URL:</strong>{' '}
                <code>{API_CONFIG.SERVER_API_URL.replace(/\/$/, '')}</code>
              </li>
              <li>
                <strong className="text-foreground">Authentication:</strong> All
                requests must include the API key in the <code>x-api-key</code>{' '}
                header.
              </li>
            </ul>
          </section>

          {/* Authentication */}
          <section id="authentication" className="scroll-mt-6 mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Authentication
            </h2>
            <DocTable
              headers={['Header', 'Type', 'Required', 'Description']}
              rows={[
                [
                  'x-api-key',
                  'string',
                  'Yes',
                  'Workspace API key for the app.',
                ],
              ]}
              className="mb-4"
            />
            <p className="text-sm text-muted-foreground mb-2">Example:</p>
            <CodeBlock language="http">
              {'x-api-key: your_api_key_here'}
            </CodeBlock>
            <p className="mt-3 text-sm text-muted-foreground">
              <strong className="text-foreground">
                Response when missing or invalid:
              </strong>{' '}
              <code>401 Unauthorized</code> (no body).
            </p>
          </section>

          {/* Upsert Contact */}
          <section id="upsert-contact" className="scroll-mt-6 mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              1. Upsert Contact
            </h2>
            <p className="text-muted-foreground mb-4">
              Create a new contact or update an existing one by identifier. If a
              contact exists, it is updated; otherwise a new one is created.
            </p>

            <div className="flex items-center gap-2 mb-3">
              <MethodBadge method="POST" />
              <code className="font-mono text-sm text-foreground">
                {API_CONFIG.SERVER_API_URL.replace(/\/$/, '')}/contacts
              </code>
            </div>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Request Headers
            </h3>
            <DocTable
              headers={['Header', 'Required', 'Description']}
              rows={[
                ['Content-Type', 'Yes', 'application/json'],
                ['x-api-key', 'Yes', 'API key (see Authentication).'],
              ]}
              className="mb-4"
            />

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Request Body
            </h3>
            <DocTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                [
                  'identifier',
                  'object',
                  'Yes',
                  'Exactly one of id, email, or custom_id. Used to find or create the contact.',
                ],
                ['identifier.id', 'string', 'No*', 'Internal contact ID.'],
                ['identifier.email', 'string', 'No*', 'Email address.'],
                [
                  'identifier.custom_id',
                  'string',
                  'No*',
                  'Your external ID for the contact.',
                ],
                [
                  'attributes',
                  'object',
                  'Yes',
                  'Contact fields and custom attributes. Values: string, number, boolean, object, array, or null.',
                ],
              ]}
              className="mb-2"
            />
            <p className="text-xs text-muted-foreground mb-4">
              * Exactly one of id, email, or custom_id must be provided.
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              Reserved attribute keys (stored as top-level):{' '}
              <code className="text-foreground">
                firstName, lastName, email, custom_id, source, preferences,
                unsubscribed, segments, createdAt, updatedAt
              </code>
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Example: Create/update by email
            </h3>
            <CodeBlock language="json" className="mb-4">
              {`{
  "identifier": {
    "email": "user@example.com"
  },
  "attributes": {
    "firstName": "Jane",
    "lastName": "Doe",
    "plan": "premium",
    "signupDate": "2024-01-15"
  }
}`}
            </CodeBlock>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Example: Create/update by custom_id
            </h3>
            <CodeBlock language="json" className="mb-4">
              {`{
  "identifier": {
    "custom_id": "ext-user-123"
  },
  "attributes": {
    "firstName": "Jane",
    "lastName": "Doe",
    "email": "jane@example.com",
    "plan": "free"
  }
}`}
            </CodeBlock>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Success Responses
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              <strong className="text-foreground">201 – Contact created</strong>
            </p>
            <CodeBlock language="json" className="mb-3">
              {`{
  "message": "Contact created successfully",
  "contact": { ... }
}`}
            </CodeBlock>
            <p className="text-sm text-muted-foreground mb-2">
              <strong className="text-foreground">200 – Contact updated</strong>
            </p>
            <CodeBlock language="json" className="mb-4">
              {`{
  "message": "Contact updated successfully",
  "contact": { ... }
}`}
            </CodeBlock>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Error Responses
            </h3>
            <DocTable
              headers={['Status', 'Condition', 'Body (typical)']}
              rows={[
                [
                  '400',
                  'Missing identifier',
                  'At least one identifier (id, email, or custom_id) is required',
                ],
                [
                  '400',
                  'More than one identifier',
                  'Only one identifier is allowed',
                ],
                [
                  '400',
                  'Email already used by another contact',
                  'Email conflict',
                ],
                [
                  '400',
                  'custom_id already used by another contact',
                  'custom_id conflict',
                ],
                ['401', 'Missing or invalid API key', 'No body'],
                ['500', 'Server error', 'Internal server error'],
              ]}
            />
          </section>

          {/* Create Event */}
          <section id="create-event" className="scroll-mt-6 mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              2. Create Event
            </h2>
            <p className="text-muted-foreground mb-4">
              Track an event for a contact. Identify by id, email, or custom_id.
              Optional deduplicationId prevents duplicate event records.
              Event-triggered workflows may start automatically.
            </p>

            <div className="flex items-center gap-2 mb-3">
              <MethodBadge method="POST" />
              <code className="font-mono text-sm text-foreground">
                {API_CONFIG.SERVER_API_URL.replace(/\/$/, '')}/events
              </code>
            </div>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Request Headers
            </h3>
            <DocTable
              headers={['Header', 'Required', 'Description']}
              rows={[
                ['Content-Type', 'Yes', 'application/json'],
                ['x-api-key', 'Yes', 'API key (see Authentication).'],
              ]}
              className="mb-4"
            />

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Request Body
            </h3>
            <DocTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                ['event', 'string', 'Yes', 'Event name (non-empty, trimmed).'],
                [
                  'identifier',
                  'object',
                  'Yes',
                  'Exactly one of id, email, or custom_id.',
                ],
                ['identifier.id', 'string', 'No*', 'Internal contact ID.'],
                ['identifier.email', 'string', 'No*', 'Contact email.'],
                [
                  'identifier.custom_id',
                  'string',
                  'No*',
                  'External contact ID.',
                ],
                [
                  'deduplicationId',
                  'string',
                  'No',
                  'Only one event with this ID per (event + contact + workspace). Duplicates return 409.',
                ],
                [
                  'data',
                  'object',
                  'No',
                  'Custom event payload for conditions and attributes.',
                ],
                [
                  'timestamp',
                  'number',
                  'No',
                  'Event time (e.g. Unix ms). Overrides data.timestamp if both sent.',
                ],
                ['sentAt', 'number', 'No', 'Sent time (e.g. Unix ms).'],
                [
                  'type',
                  'string',
                  'No',
                  'Event type. Overrides data.type if both sent.',
                ],
              ]}
              className="mb-2"
            />
            <p className="text-xs text-muted-foreground mb-4">
              * Exactly one of id, email, or custom_id must be provided.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Example: Minimal (event + identifier)
            </h3>
            <CodeBlock language="json" className="mb-4">
              {`{
  "event": "purchase_completed",
  "identifier": {
    "email": "user@example.com"
  }
}`}
            </CodeBlock>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Example: With custom data and deduplication
            </h3>
            <CodeBlock language="json" className="mb-4">
              {`{
  "event": "purchase_completed",
  "identifier": {
    "custom_id": "ext-user-123"
  },
  "deduplicationId": "order-abc-001",
  "data": {
    "orderId": "order-abc-001",
    "amount": 99.99,
    "currency": "USD",
    "items": ["item1", "item2"]
  },
  "timestamp": 1707897600000
}`}
            </CodeBlock>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Success Response
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              <strong className="text-foreground">200 – Event recorded</strong>
            </p>
            <CodeBlock language="json" className="mb-4">
              {`{
  "message": "Event sent successfully"
}`}
            </CodeBlock>

            <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
              Error Responses
            </h3>
            <DocTable
              headers={['Status', 'Condition', 'Body (typical)']}
              rows={[
                ['400', 'No identifier', 'At least one identifier required'],
                [
                  '400',
                  'More than one identifier',
                  'Only one identifier allowed',
                ],
                ['400', 'Empty event name', 'Event name is required'],
                ['401', 'Missing or invalid API key', 'No body'],
                [
                  '404',
                  'Workspace not found or user not in workspace',
                  'not found Workspace',
                ],
                ['404', 'Contact not found', 'not found Contact'],
                [
                  '409',
                  'Duplicate event (same deduplicationId + event + contact + workspace)',
                  'Event already exist',
                ],
                ['500', 'Server error', 'Internal server error'],
              ]}
            />
          </section>

          {/* Validation Summary */}
          <section className="mb-12 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4">
            <div className="flex gap-2">
              <AlertCircle className="w-5 h-5 shrink-0 text-[hsl(var(--info))]" />
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  Validation Summary
                </h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>
                    <strong className="text-foreground">Contacts:</strong>{' '}
                    identifier must have exactly one of id, email, or custom_id.
                    attributes can have any keys; values: string, number,
                    boolean, object, array, or null.
                  </li>
                  <li>
                    <strong className="text-foreground">Events:</strong> event
                    and identifier required. identifier: exactly one of id,
                    email, or custom_id. data can be any object. Invalid types
                    or extra fields → 400.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Quick Reference */}
          <section id="quick-reference" className="scroll-mt-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Quick Reference
            </h2>
            <DocTable
              headers={['API', 'Method', 'Path', 'Auth', 'Purpose']}
              rows={[
                [
                  'Upsert Contact',
                  'POST',
                  `${API_CONFIG.SERVER_API_URL.replace(/\/$/, '')}/contacts`,
                  'API Key',
                  'Create or update a contact',
                ],
                [
                  'Create Event',
                  'POST',
                  `${API_CONFIG.SERVER_API_URL.replace(/\/$/, '')}/events`,
                  'API Key',
                  'Track an event for a contact',
                ],
              ]}
            />
            <p className="mt-4 text-sm text-muted-foreground">
              Use the same API key for all requests; it is scoped to a
              workspace. Contacts and events are associated with that workspace.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ApiDocs;
